/* ------------------------------------------------------------------ hero -- */
.hero {
  position: relative;
  inline-size: 100%;
  background: var(--tint-ink);
  overflow: hidden;
}

/* The bottom edge of the hero is where the picture stops and the ledger
   starts; a hairline there is what a mounted plate has and a screenshot
   does not. */
.hero::after {
  content: "";
  position: absolute;
  inset-inline: 0;
  inset-block-end: 0;
  block-size: 1px;
  background: var(--border);
  z-index: 2;
}

.heroFrame {
  position: relative;
  inline-size: 100%;
  aspect-ratio: 4 / 3;
  display: grid;
  place-items: center;
  color: var(--ink-muted);
}

/* Scroll-driven: the plate recedes into the wall as the record scrolls over
   it. No listener, no re-render per frame, and where the timeline is missing
   the hero simply holds still. */
@supports (animation-timeline: scroll()) {
  @media (prefers-reduced-motion: no-preference) {
    .heroFrame {
      animation: hero-recede linear both;
      animation-timeline: scroll(nearest block);
      animation-range: 0 55vh;
    }
  }
}

@keyframes hero-recede {
  to {
    opacity: 0.45;
    transform: scale(0.99);
  }
}

.heroMedia {
  position: absolute;
  inset: 0;
  inline-size: 100%;
  block-size: 100%;
  object-fit: contain;
}

/* The thumbnail arrives first and is blurred up while the full asset decodes. */
.heroBlur {
  position: absolute;
  inset: 0;
  inline-size: 100%;
  block-size: 100%;
  object-fit: cover;
  filter: blur(28px) saturate(1.15);
  transform: scale(1.15);
  opacity: 0.9;
}

.heroVideo {
  position: absolute;
  inset: 0;
  inline-size: 100%;
  block-size: 100%;
  object-fit: contain;
  background: var(--ink);
}

/* ----------------------------------------------------------------- strip -- */
/*
 * The rest of the shoot, laid out under the hero the way loose prints sit under
 * the one that got hung. Scrolls sideways and snaps; the hero swaps in place
 * rather than opening anything.
 */
.strip {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  overflow-x: auto;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
}

.strip::-webkit-scrollbar {
  display: none;
}

.stripTile {
  position: relative;
  flex: none;
  inline-size: 56px;
  block-size: 56px;
  border-radius: var(--radius-chip);
  border: 1px solid var(--border);
  background: var(--tint-ink);
  color: var(--ink-muted);
  display: grid;
  place-items: center;
  overflow: hidden;
  scroll-snap-align: start;
  transition: box-shadow var(--duration-ui) var(--ease);
}

.stripTileOn {
  box-shadow: inset 0 0 0 2px var(--ink);
}

.stripPreview {
  inline-size: 100%;
  block-size: 100%;
  object-fit: cover;
}

/* ----------------------------------------------------------------- title -- */
/*
 * The wall label. Title, then who and when, then a rule — the same three parts
 * a card beside a hung work has, in the same order.
 */
.titleBlock {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-5) var(--space-4) var(--space-4);
  border-block-end: 1px solid var(--border);
  margin-block-end: calc(var(--space-2) * -1);
}

.title {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.015em;
  text-wrap: balance;
}

.titleLatin {
  font-family: var(--font-display);
  font-style: italic;
  font-size: 1.625rem;
  line-height: 1.2;
  letter-spacing: -0.005em;
}

.subtitle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  color: var(--ink-muted);
  font-size: var(--text-body-sm);
}

.dot {
  inline-size: 3px;
  block-size: 3px;
  border-radius: var(--radius-pill);
  background: currentColor;
  opacity: 0.6;
}

/* ------------------------------------------------------------------ rows -- */
/* Full-bleed inside the section card, so a rule runs the width of the sheet
   the way a ruled ledger page does. */
.rows {
  display: grid;
  margin-inline: calc(var(--space-4) * -1);
  margin-block: calc(var(--space-1) * -1);
}

.row {
  display: grid;
  grid-template-columns: 40% 1fr;
  gap: var(--space-3);
  align-items: baseline;
  padding: var(--space-3) var(--space-4);
  border-block-end: 1px solid var(--border);
}

.row:last-child {
  border-block-end: none;
}

.rowLabel {
  font-size: var(--text-body-sm);
  color: var(--ink-muted);
}

.rowValue {
  min-inline-size: 0;
  overflow-wrap: anywhere;
}

.rowValueMuted {
  color: var(--ink-muted);
  font-size: var(--text-caption);
}

.tagList {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

.tag {
  padding-inline: var(--space-2);
  padding-block: 2px;
  border-radius: var(--radius-chip);
  background: var(--tint-ink);
  font-size: var(--text-caption);
}

/* ------------------------------------------------------------- valuation -- */
/*
 * The plaque. Brass marks money and nothing else in the app, so this is the
 * only place in the interface it appears as a surface rather than a hairline.
 */
.valuation {
  display: grid;
  padding-inline: var(--space-3);
  border-radius: var(--radius-card);
  border: 1px solid color-mix(in srgb, var(--brass) 70%, transparent);
  background: var(--tint-brass);
  box-shadow: inset 0 1px 0 var(--paper-edge);
}

.valuationRow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
  padding-block: var(--space-3);
  border-block-end: 1px solid color-mix(in srgb, var(--brass) 35%, transparent);
}

.valuationRow:last-child {
  border-block-end: none;
}

.valuationLabel {
  color: var(--ink-muted);
  font-size: var(--text-body-sm);
}

.valuationAmount {
  font-family: var(--font-mono);
  font-size: 1rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  direction: ltr;
  unicode-bidi: isolate;
}

/* The worked-out price: the one figure the breakdown adds up to. */
.valuationTotal .valuationLabel {
  color: var(--ink);
  font-weight: 600;
}

.valuationTotal .valuationAmount {
  font-size: 1.25rem;
  font-weight: 600;
}

.valuationStack {
  display: grid;
  gap: var(--space-2);
}

/* --------------------------------------------------------------- status -- */
/* Surface, hairline and lift come from the global `.paper` class. */
.statusChip {
  display: inline-flex;
  justify-self: start;
  align-items: center;
  gap: var(--space-2);
  padding-inline: var(--space-3);
  padding-block: var(--space-1);
  border-radius: var(--radius-pill);
  font-size: var(--text-caption);
  font-weight: 500;
}

/* Status is never colour alone: the dot is a second channel beside the label. */
.statusDot {
  inline-size: 8px;
  block-size: 8px;
  border-radius: var(--radius-pill);
  background: var(--ink-muted);
  flex: none;
}

.notes {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: var(--ink);
  line-height: 1.6;
}

/* An evidence disclaimer, never a verdict. */
.evidenceNote {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-3);
  border-radius: var(--radius-card);
  background: var(--tint-ink);
  color: var(--ink-muted);
  font-size: var(--text-caption);
  line-height: 1.55;
}

.evidenceNote svg {
  flex: none;
  margin-block-start: 1px;
}

/* ----------------------------------------------------------- attachments -- */
.files {
  display: grid;
  gap: var(--space-2);
}

/* Surface, hairline and lift come from the global `.paper` class. */
.file {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  inline-size: 100%;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-card);
  text-align: start;
}

.fileMark {
  display: grid;
  place-items: center;
  flex: none;
  inline-size: 40px;
  block-size: 40px;
  border-radius: var(--radius-chip);
  background: var(--tint-ink);
  color: var(--ink-muted);
  overflow: hidden;
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--ink) 8%, transparent);
}

.filePreview {
  inline-size: 100%;
  block-size: 100%;
  object-fit: cover;
}

.fileBody {
  flex: 1;
  min-inline-size: 0;
  display: grid;
  gap: 1px;
}

.fileName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-body-sm);
}

.fileMeta {
  font-family: var(--font-mono);
  font-size: var(--text-micro);
  color: var(--ink-muted);
  direction: ltr;
  unicode-bidi: isolate;
}

.fileCaption {
  font-size: var(--text-caption);
  color: var(--ink-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ----------------------------------------------------------------- meta -- */
/* The colophon: what the record is, not what the work is. Set quietly, ruled
   off from the entry above it. */
.footMeta {
  display: grid;
  gap: var(--space-1);
  margin-block-start: var(--space-5);
  padding: var(--space-4) var(--space-1) 0;
  border-block-start: 1px solid var(--border);
  color: var(--ink-muted);
  font-size: var(--text-caption);
}

.idLine {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: var(--text-micro);
  direction: ltr;
  unicode-bidi: isolate;
  overflow-wrap: anywhere;
  vertical-align: baseline;
}

.deleteRow {
  padding: var(--space-5) 0 var(--space-6);
}
